/**
 * Sentry feature tagging — wrapper que classifica erros por área do produto
 * pra alert rules ficarem úteis (ver docs/SENTRY-ALERT-RULES.md).
 *
 * Uso:
 *   try {
 *     await uploadToDrive(...)
 *   } catch (err) {
 *     captureFeatureError(err, 'drive', { tenantId, fileId })
 *     throw err
 *   }
 *
 * Tags possíveis (manter em sync com alert rules):
 *   - drive       — upload/download/preview, OAuth Drive
 *   - auth        — login, signOut, refresh session
 *   - impersonation — start/end impersonation, god mode
 *   - briefing    — extract rules, compliance analysis
 *   - delivery    — CRUD entrega, status transitions, audit logs
 *   - billing     — invoice PDF, PO export, payment status
 *   - admin       — platform admin RPCs (suspend tenant, etc)
 */

import * as Sentry from '@sentry/react'

export type FeatureTag =
  | 'drive'
  | 'auth'
  | 'impersonation'
  | 'briefing'
  | 'delivery'
  | 'billing'
  | 'admin'
  | 'unknown'

/**
 * Captura erro com tag de feature + context extra.
 * Não re-throw — caller decide.
 */
export function captureFeatureError(
  error: unknown,
  feature: FeatureTag,
  context?: Record<string, unknown>,
): void {
  Sentry.withScope(scope => {
    scope.setTag('feature', feature)
    if (context) scope.setContext('feature_context', context as Record<string, unknown>)
    if (error instanceof Error) {
      Sentry.captureException(error)
    } else {
      Sentry.captureException(new Error(typeof error === 'string' ? error : JSON.stringify(error)))
    }
  })
}

/**
 * Define tag de feature na scope ATUAL (transaction/breadcrumbs).
 * Útil pra wrap código sem capturar erro (mantém contexto se algo quebrar).
 */
export function withFeatureScope<T>(feature: FeatureTag, fn: () => T): T {
  return Sentry.withScope(scope => {
    scope.setTag('feature', feature)
    return fn()
  })
}
